import { ClockIcon, MapPinIcon, UserIcon } from "lucide-react"
import { createContext, use, useCallback, useState, type ReactNode } from "react"
import { Link } from "react-router"
import { groupChipsOf } from "@/administration/describe.ts"
import { Badge } from "@/primitives/badge.tsx"
import { Button } from "@/primitives/button.tsx"
import { Drawer, DrawerBody, DrawerContent, DrawerTitle } from "@/primitives/drawer.tsx"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/primitives/hover-card.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { initialsOf, localTimeOf, markerOf, nameShown } from "@/people/describe.ts"
import { clickHostOf, type ClickHost } from "./click-host.ts"
import { hueNames, identity, identityOf, type Identity } from "./identity.ts"
import { Toggletip } from "./toggletip.tsx"
import { useViewport } from "./viewport.tsx"

/*
 * Who or what something is, at a glance: a Member's avatar, a Project's tile and a Group's dot,
 * each in its marker colour: a Member's hashed from their id, a Project's from its key, a Group's
 * from its id. The hue tells two apart and means nothing else. Every size is a closed choice, and
 * each maps to classes written out whole.
 *
 * A Member's avatar shows their photo where they have one. Which Members do comes from the
 * `PhotoDirectory` the shell wraps every page in, so a page passes a Member as it always has (an
 * id and a name) and never asks for photos itself. Their hover card's job title, location and
 * local time come the same way, from the `PeopleDirectory`.
 */

/** A Member as an avatar shows them: their id, for their marker colour and photo, and their name. */
export type PersonShown = { id: string; name: string }

/** Every Member's photo by their id: the URL its sizes are under (`/48.webp` and the rest). */
const PhotosContext = createContext<Readonly<Record<string, string>>>({})

/** Gives every avatar beneath it the photos it shows: the web API's `/people/photos`. */
export function PhotoDirectory({
  photos,
  children,
}: {
  photos: Readonly<Record<string, string>>
  children: ReactNode
}) {
  return <PhotosContext value={photos}>{children}</PhotosContext>
}

/**
 * What a person's hover card says of them beyond their name: as the People page has them.
 * `deactivatedAt` marks a former Member, whom every name of theirs says is one.
 */
export type PersonCard = {
  pronouns: string | null
  jobTitle: string | null
  location: string | null
  timeZone: string | null
  deactivatedAt?: string | null
}

type People = { cards: Readonly<Record<string, PersonCard>>; viewerZone: string }

const PeopleContext = createContext<People | undefined>(undefined)

/**
 * Gives every avatar and Member row beneath it the hover cards it opens: the web API's `/people`,
 * and the viewer's own time zone for how far each person's time is from theirs.
 */
export function PeopleDirectory({ cards, viewerZone, children }: People & { children: ReactNode }) {
  return <PeopleContext value={{ cards, viewerZone }}>{children}</PeopleContext>
}

/** Whether the `PeopleDirectory` holds a person as a former Member: deactivated. */
function useFormer(person: PersonShown | null) {
  const people = use(PeopleContext)
  return person !== null && Boolean(people?.cards[person.id]?.deactivatedAt)
}

/**
 * A Member's name as a line of history shows it, marked "(former)" for one the `PeopleDirectory`
 * holds as deactivated, or "A former Member" for one gone (`null`).
 */
export function PersonName({ person }: { person: PersonShown | null }) {
  return <>{nameShown(person, useFormer(person))}</>
}

/**
 * The viewer's own time zone, by which a person's local time says how far it is from theirs: their
 * Profile's, else this browser's.
 */
export function useViewerZone() {
  return use(PeopleContext)?.viewerZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * A person's card, opened by resting the pointer on `children`: their photo, name and pronouns,
 * job title, location and the time where they are, with their profile a click away. Outside a
 * `PeopleDirectory`, or for someone it doesn't hold, `fallback` shows instead.
 *
 * Touch rests no pointer, so there a tap on `children` opens the card as a sheet, as a Toggletip
 * opens: inside a row (a ticket's, a DataTable's) the tap is the name's and the row stays put,
 * while inside a control (an assignee Picker's button) the control keeps it, since what it opens
 * shows the person too.
 */
function PersonHoverCard({
  person,
  children,
  fallback,
}: {
  person: PersonShown
  children: ReactNode
  fallback: ReactNode
}) {
  const { touch } = useViewport()
  const people = use(PeopleContext)
  const card = people?.cards[person.id]
  if (!people || !card) return fallback
  const local = localTimeOf(card.timeZone, new Date(), people.viewerZone)
  const contents = (
    <>
      <div className="flex gap-3">
        <Disc person={person} size={32} />
        <div className="min-w-0 space-y-0.5">
          <p className="truncate">
            <Link
              to={`/people/${person.id}`}
              className="font-medium text-foreground underline-offset-2 hover:underline"
            >
              {person.name}
            </Link>
            {card.pronouns && (
              <span className="ml-1.5 text-subtle-foreground">{card.pronouns}</span>
            )}
          </p>
          {card.jobTitle && <p className="line-clamp-2 text-muted-foreground">{card.jobTitle}</p>}
          {card.deactivatedAt && <p className="text-xs text-subtle-foreground">Former Member</p>}
        </div>
      </div>
      {(card.location || local) && (
        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          {card.location && (
            <p className="flex items-center gap-1.5">
              <MapPinIcon aria-hidden className="size-3.5 shrink-0" />
              <span className="truncate">{card.location}</span>
            </p>
          )}
          {local && (
            <p className="flex items-center gap-1.5">
              <ClockIcon aria-hidden className="size-3.5 shrink-0" />
              <span className="tabular-nums">{local.time}</span>
              <span className="text-subtle-foreground">· {local.difference}</span>
            </p>
          )}
        </div>
      )}
    </>
  )
  if (touch)
    return (
      <PersonCardSheet name={person.name} contents={contents}>
        {children}
      </PersonCardSheet>
    )
  return (
    <HoverCard>
      <HoverCardTrigger render={<span className="inline-flex min-w-0 align-middle" />}>
        {children}
      </HoverCardTrigger>
      <HoverCardContent>{contents}</HoverCardContent>
    </HoverCard>
  )
}

/**
 * The person card on touch: a sheet a tap on their name opens, unless a control holds the name (a
 * button, a Picker's option), which keeps the tap.
 */
function PersonCardSheet({
  name,
  contents,
  children,
}: {
  name: string
  contents: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [host, setHost] = useState<ClickHost>("none")
  const measure = useCallback((trigger: HTMLElement | null) => {
    if (trigger) setHost(clickHostOf(trigger))
  }, [])
  return (
    <span
      ref={measure}
      className="inline-flex min-w-0 align-middle"
      onClick={(event) => {
        // The sheet is portalled out of the row, but its clicks still bubble here through React's
        // tree: they are the sheet's, and never reach the row.
        if (!event.currentTarget.contains(event.target as Node)) return event.stopPropagation()
        if (host === "control") return
        event.preventDefault()
        event.stopPropagation()
        setOpen(true)
      }}
    >
      {children}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerTitle className="sr-only">{name}</DrawerTitle>
          {/* Following the link to their profile leaves the card behind. */}
          <DrawerBody
            className="pt-2 pb-6"
            onClickCapture={(event) => {
              if (event.target instanceof Element && event.target.closest("a")) setOpen(false)
            }}
          >
            {contents}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </span>
  )
}

/*
 * Each disc size, and the photo size it draws: at least twice its pixels, so a photo stays sharp
 * on a dense screen.
 */
const discSizes = {
  16: { size: "size-4", photo: 48 },
  20: { size: "size-5 text-2xs", photo: 48 },
  24: { size: "size-6 text-2xs", photo: 48 },
  32: { size: "size-8 text-xs", photo: 96 },
  64: { size: "size-16 text-title", photo: 256 },
} as const

type DiscSize = keyof typeof discSizes

/**
 * A Member's disc: their photo, or else their initials on their marker colour, or a quiet figure
 * for a former Member (`null`). At 16px there is no room for letters, so the disc stands alone and
 * its label names them. A photo that fails to load falls back to the initials.
 */
export function Disc({
  person,
  size,
  ring,
}: {
  person: PersonShown | null
  size: DiscSize
  ring?: string
}) {
  const photos = use(PhotosContext)
  const [failed, setFailed] = useState<string>()
  const hue = person === null ? undefined : identity[markerOf(person)]
  const photo = person === null ? undefined : photos[person.id]
  const src = photo && `${photo}/${discSizes[size].photo}.webp`
  return (
    <span
      role="img"
      aria-label={nameShown(person)}
      className={cn(
        "inline-grid shrink-0 place-items-center overflow-hidden rounded-full font-semibold select-none",
        discSizes[size].size,
        hue ? [hue.solid, "text-identity-foreground"] : "bg-muted text-subtle-foreground",
        ring,
      )}
    >
      {src && failed !== src ? (
        <img
          src={src}
          alt=""
          aria-hidden
          draggable={false}
          className="size-full object-cover"
          onError={() => setFailed(src)}
        />
      ) : person === null ? (
        <UserIcon aria-hidden className={size === 16 ? "size-2.5" : "size-3"} />
      ) : (
        size !== 16 && <span aria-hidden>{initialsOf(person.name)}</span>
      )}
    </span>
  )
}

/**
 * A Member's avatar, opening their hover card, or with their name in a tooltip for someone the
 * `PeopleDirectory` doesn't hold. `null` is a former Member.
 */
export function Avatar({
  person,
  size,
}: {
  person: PersonShown | null
  size: Exclude<DiscSize, 64>
}) {
  const former = useFormer(person)
  const named = (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex shrink-0" />}>
        <Disc person={person} size={size} />
      </TooltipTrigger>
      <TooltipContent>{nameShown(person, former)}</TooltipContent>
    </Tooltip>
  )
  if (!person) return named
  return (
    <PersonHoverCard person={person} fallback={named}>
      <Disc person={person} size={size} />
    </PersonHoverCard>
  )
}

/*
 * The surface a stack is ringed in. `selected` is a chosen row's fill, as the Groups list's chosen
 * group. `background-row` and `card-row` are a row of a list on the page or in a panel that fills
 * with the accent under the pointer, the keyboard's highlight and a Tab focus (`rowHighlight`): the
 * ring follows the row's fill, as the row's `group/row` says, so the cut never shows the surface
 * beneath the row.
 */
const followsRow =
  "transition-shadow duration-100 ease-snappy group-hover/row:ring-accent group-focus-visible/row:ring-accent group-data-highlighted/row:ring-accent"

const stackRings = {
  background: "ring-2 ring-background",
  card: "ring-2 ring-card",
  selected: "ring-2 ring-selected",
  "background-row": `ring-2 ring-background ${followsRow}`,
  "card-row": `ring-2 ring-card ${followsRow}`,
} as const

/*
 * How far each disc tucks under the one before it. Two-letter initials fill a small disc nearly to
 * its edge, and each disc's ring already cuts 2px from its neighbour, so a stack with letters
 * overlaps by no more than keeps them apart: none at 20px, where the ring alone makes the cut, and
 * 2px at 24px. Otherwise neighbours' initials run together into one word ("SMAM"). The 16px discs
 * carry no letters and tuck further.
 */
const stackOverlaps = { 16: "-space-x-1", 20: "", 24: "-space-x-0.5" } as const

const overflowSizes = {
  16: "size-4 text-2xs",
  20: "size-5 text-2xs",
  24: "size-6 text-2xs",
} as const

/** How many avatars a stack shows before it says "+N". */
const shownInStack = 3

/**
 * Several Members, overlapping: the first three, then "+N" for the rest, with every name in one
 * Toggletip. Each disc is ringed in the surface it sits `on`, so the overlap reads as a cut. Nobody
 * renders nothing, so a caller keeps an empty slot of its own.
 */
export function AvatarStack({
  people,
  size,
  on,
}: {
  people: readonly PersonShown[]
  size: 16 | 20 | 24
  on: keyof typeof stackRings
}) {
  if (people.length === 0) return null
  const shown = people.slice(0, shownInStack)
  const rest = people.length - shown.length
  return (
    <Toggletip
      tip={
        <span className="flex flex-col items-start gap-0.5">
          {people.map(({ id, name }) => (
            <span key={id}>{name}</span>
          ))}
        </span>
      }
    >
      {/* One image named for everyone in it: a name on a plain span is ignored, and the "+N"
          disc says nothing to a screen reader, so the discs inside are presentational. */}
      <span
        role="img"
        aria-label={people.map(({ name }) => name).join(", ")}
        className={cn("inline-flex shrink-0", stackOverlaps[size])}
      >
        {shown.map((person) => (
          <Disc key={person.id} person={person} size={size} ring={stackRings[on]} />
        ))}
        {rest > 0 && (
          <span
            aria-hidden
            className={cn(
              "inline-grid shrink-0 place-items-center rounded-full bg-muted font-medium text-muted-foreground tabular-nums",
              overflowSizes[size],
              stackRings[on],
            )}
          >
            +{rest}
          </span>
        )}
      </span>
    </Toggletip>
  )
}

/**
 * A Member as a row names them: their avatar and their name, which open their hover card, and
 * "You" on the signed-in Member's own row. `detail` follows the name in the quiet tier: their email
 * where a page lists Members to manage them, their job title on People. `size` "none" leaves the
 * avatar out, where a stacked row's lead already shows it. `card={false}` leaves the hover card
 * out, where a tap on the name belongs to a row that opens the person's own page. `wrap` lets the
 * name run to a second line on `compact` rather than be cut, where a row has the height for it.
 */
export function MemberIdentity({
  person,
  detail,
  size,
  you = false,
  card = true,
  wrap = false,
}: {
  person: PersonShown
  detail?: string | null
  size: 20 | 24 | "none"
  you?: boolean
  card?: boolean
  wrap?: boolean
}) {
  const former = useFormer(person)
  const named = (
    <span className="inline-flex min-w-0 items-center gap-2">
      {size !== "none" && <Disc person={person} size={size} />}
      <span
        className={cn(
          "min-w-0 truncate font-medium text-foreground",
          wrap && "compact:line-clamp-2 compact:whitespace-normal compact:wrap-break-word",
        )}
      >
        {person.name}
      </span>
    </span>
  )
  return (
    // Middle-aligned, as a photo has no text baseline and would sit higher than initials do.
    <span className="inline-flex min-w-0 items-center gap-2 align-middle text-ui">
      {card ? (
        <PersonHoverCard person={person} fallback={named}>
          {named}
        </PersonHoverCard>
      ) : (
        named
      )}
      {detail && detail !== person.name && (
        // On `compact`, where the width runs out first, the detail takes only what the name leaves.
        <span className="min-w-0 truncate text-subtle-foreground compact:flex-1">{detail}</span>
      )}
      {you && <Badge>You</Badge>}
      {former && <Badge variant="neutral">Former</Badge>}
    </span>
  )
}

/*
 * A tile up to 28px carries its letter white on its hue's solid fill, as an avatar does: at 11 or
 * 12px a letter in the hue on its pale tint is too faint to read, most of all in dark. The 40px
 * tile's letter is large enough to sit on the tint, and it keeps that quieter look.
 */
const tileSizes = {
  16: { size: "size-4 rounded-sm text-2xs", fill: "solid" },
  20: { size: "size-5 rounded-sm text-2xs", fill: "solid" },
  28: { size: "size-7 rounded-md text-xs", fill: "solid" },
  40: { size: "size-10 rounded-lg text-title", fill: "tint" },
} as const

/**
 * A Project's tile: the first letter of its key on its hue. It decorates a name printed beside
 * it, so a screen reader skips it.
 */
export function IdentityTile({
  projectKey,
  size,
}: {
  projectKey: string
  size: 16 | 20 | 28 | 40
}) {
  const hue = identity[identityOf(projectKey)]
  const { size: sized, fill } = tileSizes[size]
  return (
    <span
      aria-hidden
      className={cn(
        "inline-grid shrink-0 place-items-center font-semibold select-none",
        sized,
        fill === "solid"
          ? [hue.solid, "text-identity-foreground"]
          : [hue.tint, "ring-1 ring-inset"],
      )}
    >
      {projectKey.charAt(0).toUpperCase()}
    </span>
  )
}

/** A Group's dot, in the hue hashed from `seed` (its id), beside its name. */
export function GroupDot({ seed }: { seed: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block size-2 shrink-0 rounded-full", identity[identityOf(seed)].dot)}
    />
  )
}

/**
 * A dot in a chosen hue, such as an Area's, beside its name; with no hue, an empty ring, for none
 * ("No Area").
 */
export function HueDot({ hue }: { hue: Identity | undefined }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-2 shrink-0 rounded-full",
        hue ? identity[hue].dot : "ring-1 ring-border-strong",
      )}
    />
  )
}

/**
 * The dots of several chosen hues side by side, such as the Labels a ticket carries, beside their
 * names: the first three, since the names say the rest; with none, the one empty ring.
 */
export function HueDots({ hues }: { hues: readonly Identity[] }) {
  if (hues.length < 2) return <HueDot hue={hues[0]} />
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center gap-0.5">
      {hues.slice(0, 3).map((hue, index) => (
        <HueDot key={index} hue={hue} />
      ))}
    </span>
  )
}

const hues = Object.keys(hueNames).map(Number) as Identity[]

/**
 * A chosen hue, as a dot on a quiet button that opens the eight to choose from. Choosing one closes
 * it; the one chosen already is ringed. `label` names what is being coloured ("Payments's colour").
 */
export function HuePicker({
  value,
  label,
  onChange,
}: {
  value: Identity
  label: string
  onChange: (hue: Identity) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        // Its hit area overhangs 6px each side, so the dot stands where a row's 16px glyph does.
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            className="-mx-1.5"
            aria-label={`${label}: ${hueNames[value]}`}
          />
        }
      >
        <span className={cn("size-3 rounded-full", identity[value].dot)} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-0 p-2" title={label}>
        <div role="radiogroup" aria-label={label} className="grid grid-cols-4 gap-1">
          {hues.map((hue) => (
            <button
              key={hue}
              type="button"
              role="radio"
              aria-checked={hue === value}
              aria-label={hueNames[hue]}
              title={hueNames[hue]}
              onClick={() => {
                setOpen(false)
                if (hue !== value) onChange(hue)
              }}
              className="grid size-7 place-items-center rounded-md outline-none hover:bg-accent touch:size-11 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={cn(
                  "size-4 rounded-full",
                  identity[hue].dot,
                  hue === value && "ring-2 ring-ring ring-offset-2 ring-offset-popover",
                )}
              />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * A Member's groups, each a chip with its dot and name: the first three, then "+N" naming the rest
 * in its Toggletip. `id` seeds the dot's hue. In a narrow cell the last chip's name truncates before
 * the "+N" goes, so the count of the rest still shows.
 */
export function GroupChips({ groups }: { groups: readonly { id: string; name: string }[] }) {
  const { shown, folded } = groupChipsOf(groups)
  return (
    <span className="flex min-w-0 items-center gap-1">
      {shown.map((group, index) => (
        <Badge
          key={group.id}
          variant="outline"
          className={cn(index === shown.length - 1 && "min-w-0 shrink")}
        >
          <GroupDot seed={group.id} />
          <span className="max-w-24 min-w-0 truncate">{group.name}</span>
        </Badge>
      ))}
      {folded.length > 0 && (
        <Toggletip tip={folded.map(({ name }) => name).join(", ")}>
          <span className="shrink-0 text-2xs text-subtle-foreground tabular-nums">
            +{folded.length}
          </span>
        </Toggletip>
      )}
    </span>
  )
}
